import { useState } from 'react'
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react'

interface RedeployResult {
  total: number
  succeeded: string[]
  failed: Array<{ tenant: string; branch: string; error: string }>
  skipped: string[]
  dryRun: boolean
}

const BulkRedeployPanel = () => {
  const [loading, setLoading] = useState(false)
  const [dryRun, setDryRun] = useState(true)
  const [selectedBranches, setSelectedBranches] = useState(['live'])
  const [result, setResult] = useState<RedeployResult | null>(null)
  const [error, setError] = useState('')

  const handleRedeploy = async () => {
    if (!dryRun) {
      const confirmed = window.confirm(
        `⚠️ You are about to trigger rebuilds for ${selectedBranches.join(', ')} branches of ALL tenant trust centers.\n\n` +
        'This will:\n' +
        '• Merge main branch into all selected tenant branches\n' +
        '• Trigger Netlify rebuilds for all affected sites\n' +
        '• Propagate security updates and code changes\n\n' +
        'Are you sure you want to continue?'
      )
      if (!confirmed) return
    }

    setLoading(true)
    setError('')
    setResult(null)

    try {
      // Get platform admin key from localStorage or prompt
      const adminKey = localStorage.getItem('platform-admin-key') || prompt('Enter Platform Admin Key:')
      if (!adminKey) {
        setError('Platform Admin Key is required')
        setLoading(false)
        return
      }

      // Store for future use
      localStorage.setItem('platform-admin-key', adminKey)

      const response = await fetch('/api/admin/bulk-redeploy-tenants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify({
          dryRun,
          branches: selectedBranches
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Bulk redeploy failed')
      }

      const data = await response.json()
      setResult(data.results)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger bulk redeploy')
      console.error('Bulk redeploy error:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleBranch = (branch: string) => {
    setSelectedBranches(prev =>
      prev.includes(branch)
        ? prev.filter(b => b !== branch)
        : [...prev, branch]
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Bulk Tenant Redeploy
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Propagate security updates and code changes to all tenant trust centers
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-2">What this does:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Merges <code className="bg-blue-100 px-1 rounded">main</code> branch into all tenant branches</li>
              <li>Triggers automatic Netlify rebuilds with latest code</li>
              <li>Propagates React security updates (CVE-2025-55182) and other fixes</li>
              <li>No downtime - Netlify handles atomic deployments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Branch Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Branches to Redeploy
        </label>
        <div className="space-y-2">
          {[
            { id: 'live', label: 'Live', description: 'Production sites (customer-facing)' },
            { id: 'preview', label: 'Preview', description: 'Staging/preview environments' },
            { id: 'last', label: 'Last', description: 'Rollback/previous version' }
          ].map(branch => (
            <label key={branch.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedBranches.includes(branch.id)}
                onChange={() => toggleBranch(branch.id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{branch.label}</div>
                <div className="text-sm text-gray-600">{branch.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Dry Run Toggle */}
      <div className="mb-6">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="rounded"
          />
          <div>
            <span className="font-medium text-gray-900">Dry Run Mode</span>
            <p className="text-sm text-gray-600">Test without making actual changes</p>
          </div>
        </label>
      </div>

      {/* Action Button */}
      <button
        onClick={handleRedeploy}
        disabled={loading || selectedBranches.length === 0}
        className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition ${
          loading || selectedBranches.length === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : dryRun
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white'
        }`}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            {dryRun ? 'Running Dry Run...' : 'Deploying to All Tenants...'}
          </>
        ) : (
          <>
            <RefreshCw className="w-5 h-5" />
            {dryRun ? 'Run Dry Run' : 'Deploy to All Tenants'}
          </>
        )}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-900">
              <p className="font-medium">Error</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-4">
            {result.dryRun ? 'Dry Run Results' : 'Deployment Results'}
          </h4>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Succeeded</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{result.succeeded.length}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Failed</span>
              </div>
              <div className="text-2xl font-bold text-red-900">{result.failed.length}</div>
            </div>
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <Info className="w-4 h-4" />
                <span className="font-medium">Skipped</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{result.skipped.length}</div>
            </div>
          </div>

          {/* Failed Deployments */}
          {result.failed.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-red-900 mb-2">Failed Deployments:</h5>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {result.failed.map((failure, idx) => (
                  <div key={idx} className="bg-white border border-red-200 rounded p-2 text-sm">
                    <div className="font-medium text-gray-900">{failure.tenant} - {failure.branch}</div>
                    <div className="text-red-700">{failure.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success Message */}
          {result.failed.length === 0 && result.succeeded.length > 0 && (
            <div className="mt-4 flex items-start gap-2 text-green-800">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium">
                  {result.dryRun
                    ? 'Dry run completed successfully!'
                    : 'All tenant sites have been updated!'}
                </p>
                <p className="mt-1">
                  {result.dryRun
                    ? 'No changes were made. Disable dry run mode to apply changes.'
                    : 'Netlify will rebuild all affected sites with the latest code from main.'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BulkRedeployPanel
